import axios from 'axios';
import { packageSuppliersApi } from './endpoints';
import aplicacionesApi from '../aplicaciones';
import { safeSessionStorage, useSessionStorage, isClient } from 'legacyfrontend/helpers/ssr-helper';

// Function to get token from aplicaciones API
const getToken = async () => {
  if (!isClient) {
    return '';
  }

  const { hostname } = isClient ? window.location : { hostname: '' };
  try {
    const res = await aplicacionesApi.obtenerAplicacion(hostname);
    return res.access_token;
  } catch (error) {
    console.error('Error getting token:', error);
    safeSessionStorage.setItem(`[TOKEN][${new Date()}]`, JSON.stringify(error));
    return '';
  }
};

const client = axios.create({
  baseURL: process.env.RAZZLE_API_PACKAGE_PROVIDERS,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to include token
client.interceptors.request.use(async (config) => {
  // If token is already in headers, use it
  if (config.headers.Authorization) {
    return config;
  }

  // Otherwise get a new token
  const token = await getToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Add response interceptor to handle 401 errors and refresh token
client.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // If error is 401 and we haven't tried to refresh token yet
    if (error.response && error.response.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      // Get new token
      const newToken = await getToken();

      // Update the request with new token
      originalRequest.headers.Authorization = `Bearer ${newToken}`;

      // Retry the request
      return client(originalRequest);
    }

    return Promise.reject(error);
  }
);

export const getPackage = async (packageId) =>
  (await client.get(packageSuppliersApi.getPackageById(packageId)))?.data;

export const getPackageConfiguration = async (packageConfigurationId) =>
  (await client.get(packageSuppliersApi.getPackageConfigurationById(packageConfigurationId)))?.data;

export const getPackageAndHotelInformation = async (packageId, hotelInformationId) =>
  (
    await client.get(
      packageSuppliersApi.getPackageByIdAndHotelInformationId(packageId, hotelInformationId)
    )
  )?.data;

export const getPackageDepartures = async (packageId, filters) =>
  (
    await client.get(packageSuppliersApi.getDeparturesByPackageId(packageId), {
      params: filters,
    })
  )?.data;

export const getPackageConfigurations = async (packageId, filters, token) => {
  // If token is provided, use it directly
  if (token) {
    const config = {
      headers: {
        'Content-type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      params: filters,
    };

    return (await client.get(packageSuppliersApi.getConfigurationsByPackageId(packageId), config))
      ?.data;
  }

  // Otherwise use the interceptor to get token
  return (
    await client.get(packageSuppliersApi.getConfigurationsByPackageId(packageId), {
      params: filters,
    })
  )?.data;
};
